import { useEffect, useState } from 'react'

const QUERY = '(prefers-reduced-motion: reduce)'

function getInitialState() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // SSR-safe default: assume motion is fine when we can't ask the browser.
    return false
  }
  return window.matchMedia(QUERY).matches
}

/**
 * Tracks the user's `prefers-reduced-motion` OS/browser setting, including
 * live updates if they change it while the page is open (e.g. via system
 * settings). SSR-safe: defaults to `false` (motion allowed) when `window`
 * or `matchMedia` aren't available.
 *
 * Used for imperative/non-Framer motion (setInterval-driven auto-scroll,
 * etc). For components already built on Framer Motion's declarative
 * `animate`/`transition` props, prefer Framer's own `useReducedMotion`
 * hook instead — it reads the same media query, so behaviour is identical.
 */
export function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(getInitialState)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined
    }

    const mediaQueryList = window.matchMedia(QUERY)
    const handleChange = (event) => setPrefersReducedMotion(event.matches)

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange)
      return () => mediaQueryList.removeEventListener('change', handleChange)
    }

    // Safari < 14 fallback
    mediaQueryList.addListener(handleChange)
    return () => mediaQueryList.removeListener(handleChange)
  }, [])

  return prefersReducedMotion
}

export default usePrefersReducedMotion
